"use client";

import { c as _c } from "react/compiler-runtime";
import { Suspense, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { GenreSidebar, type TagData } from "@/components/browse/genre-sidebar";
import { FilterShell } from "@/components/browse/filter-shell";
import { FilterEmptyState } from "@/components/browse/filter-empty-state";
import { TrendingCarousel } from "@/components/browse/trending-carousel";
import { StoryGrid } from "@/components/browse/story-grid";
import { filterCatalogStories } from "@/lib/catalog-filter";
import { mergeGenreSelection, parseDiscovery, parseGenres, parseStatus, parseTags, renderFilterUrl } from "@/lib/filter-utils";
import type { CatalogStory, StoryGenre, StoryStatus } from "@/lib/actions/story-actions";
import { crownDiscoveryFilterOption, type CrownDiscoveryFilter } from "@/lib/spotlights/discovery-filters";
import type { SpotlightDiscoveryData, SpotlightDiscoveryResult } from "@/lib/spotlights/queries";

// ─── Catalog explorer ────────────────────────────────────
// The filterable half of the browse/genre pages. The pages themselves are
// statically rendered (ISR) and embed the FULL public catalog; this client
// component reads the filter state from the URL and filters in memory
// (catalog-filter.ts mirrors the SQL semantics), so neither bot crawls nor
// human filter clicks invoke a server function.
//
// useSearchParams would normally bail the whole static page out of
// prerendering, so it's quarantined inside the Suspense boundary below: the
// fallback renders the UNFILTERED view (which is what belongs in the static
// HTML — that's also the SEO content), and the hydrated client swaps in the
// filtered view. For clean URLs the two are identical, so nothing changes
// visually.

interface ParsedFilters {
  genres: StoryGenre[];
  discovery?: CrownDiscoveryFilter;
  status?: StoryStatus;
  tags: string[];
  q?: string;
}
const NO_FILTERS: ParsedFilters = {
  genres: [],
  tags: []
};
const EMPTY_CROWN_DISCOVERY: SpotlightDiscoveryData = {
  mostSpotlighted: [],
  ascending: [],
  freshFinds: []
};
const DISABLED_CROWN_DISCOVERY: SpotlightDiscoveryResult = {
  status: "disabled"
};
interface CatalogExplorerProps {
  variant: "browse" | "genre";
  /** Full public catalog with facets, embedded at ISR time. */
  stories: CatalogStory[];
  /** Sidebar tag list. */
  tags: TagData[];
  /** Ranked Crown category membership for Browse discovery filters. */
  crownDiscovery?: SpotlightDiscoveryResult;
  /** genre variant only: the page's path genre. */
  anchorGenre?: StoryGenre;
  /** genre variant only: the path genre's slug, for clear-filter hrefs. */
  anchorSlug?: string;
}
export function CatalogExplorer(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Suspense fallback={<CatalogView {...props} filters={NO_FILTERS} />}><CatalogWithParams {...props} /></Suspense>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}

/** Reads the live URL filter state — only rendered client-side. */
function CatalogWithParams(props) {
  const $ = _c(29);
  const sp = useSearchParams();
  const router = useRouter();
  const crownDiscoveryAvailable = props.crownDiscovery?.status === "enabled";
  let rawDiscovery;
  let t0;
  if ($[0] !== sp) {
    rawDiscovery = sp.get("discovery");
    t0 = parseDiscovery(rawDiscovery ?? undefined);
    $[0] = sp;
    $[1] = rawDiscovery;
    $[2] = t0;
  } else {
    rawDiscovery = $[1];
    t0 = $[2];
  }
  const parsedDiscovery = t0;
  let t1;
  if ($[3] !== sp) {
    t1 = sp.get("q")?.trim() || undefined;
    $[3] = sp;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const q = t1;
  let t2;
  if ($[5] !== sp) {
    t2 = parseGenres(sp.getAll("genre"));
    $[5] = sp;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = props.variant === "browse" && crownDiscoveryAvailable ? parsedDiscovery : undefined;
  let t4;
  if ($[7] !== sp) {
    t4 = parseStatus(sp.get("status") ?? undefined);
    $[7] = sp;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== sp) {
    t5 = parseTags(sp.getAll("tag"));
    $[9] = sp;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== q || $[12] !== t2 || $[13] !== t3 || $[14] !== t4 || $[15] !== t5) {
    t6 = {
      genres: t2,
      discovery: t3,
      status: t4,
      tags: t5,
      q
    };
    $[11] = q;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const filters = t6;
  const variant = props.variant;
  let t7;
  let t8;
  if ($[17] !== crownDiscoveryAvailable || $[18] !== parsedDiscovery || $[19] !== q || $[20] !== rawDiscovery || $[21] !== router || $[22] !== sp || $[23] !== variant) {
    t7 = () => {
      if (variant !== "browse") {
        return;
      }
      const genres = parseGenres(sp.getAll("genre"));
      const removeDiscovery = rawDiscovery !== null && (!crownDiscoveryAvailable || !parsedDiscovery);
      if (genres.length === 0 && !removeDiscovery) {
        return;
      }
      const target = renderFilterUrl({
        genres,
        discovery: removeDiscovery ? undefined : parsedDiscovery,
        status: parseStatus(sp.get("status") ?? undefined),
        tags: parseTags(sp.getAll("tag")),
        q
      });
      router.replace(target);
    };
    t8 = [crownDiscoveryAvailable, parsedDiscovery, q, rawDiscovery, router, sp, variant];
    $[17] = crownDiscoveryAvailable;
    $[18] = parsedDiscovery;
    $[19] = q;
    $[20] = rawDiscovery;
    $[21] = router;
    $[22] = sp;
    $[23] = variant;
    $[24] = t7;
    $[25] = t8;
  } else {
    t7 = $[24];
    t8 = $[25];
  }
  useEffect(t7, t8);
  let t9;
  if ($[26] !== filters || $[27] !== props) {
    t9 = <CatalogView {...props} filters={filters} />;
    $[26] = filters;
    $[27] = props;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  return t9;
}

/** Pure presentational render of one filter state — SSR-safe. */
function CatalogView(t0) {
  const $ = _c(67);
  const {
    variant,
    stories,
    tags,
    anchorGenre,
    anchorSlug,
    crownDiscovery: t1,
    filters
  } = t0;
  const crownDiscovery = t1 === undefined ? DISABLED_CROWN_DISCOVERY : t1;
  if (variant === "genre" && anchorGenre) {
    let T0;
    let filtered;
    let hasActiveFilters;
    let t2;
    let t3;
    let t4;
    if ($[0] !== anchorGenre || $[1] !== filters.genres || $[2] !== filters.q || $[3] !== filters.status || $[4] !== filters.tags || $[5] !== stories || $[6] !== tags) {
      let t5;
      if ($[13] !== anchorGenre) {
        t5 = g => g !== anchorGenre;
        $[13] = anchorGenre;
        $[14] = t5;
      } else {
        t5 = $[14];
      }
      const extraGenres = filters.genres.filter(t5);
      const activeGenres = mergeGenreSelection(anchorGenre, extraGenres);
      filtered = filterCatalogStories(stories, {
        genres: activeGenres,
        status: filters.status,
        tags: filters.tags,
        q: filters.q
      });
      let t6;
      if ($[15] !== filters.q || $[16] !== filters.status || $[17] !== filters.tags || $[18] !== stories) {
        t6 = filterCatalogStories(stories, {
          status: filters.status,
          tags: filters.tags,
          q: filters.q
        });
        $[15] = filters.q;
        $[16] = filters.status;
        $[17] = filters.tags;
        $[18] = stories;
        $[19] = t6;
      } else {
        t6 = $[19];
      }
      const totalCount = t6.length;
      hasActiveFilters = Boolean(filters.status) || filters.tags.length > 0 || extraGenres.length > 0 || Boolean(filters.q);
      const activeFilterCount = activeGenres.length + filters.tags.length + (filters.status ? 1 : 0);
      const t7 = <FilterShell activeCount={activeFilterCount}><GenreSidebar storyCount={totalCount} activeGenres={activeGenres} activeStatus={filters.status} activeTags={filters.tags} activeQuery={filters.q} anchorGenre={anchorGenre} tags={tags} /></FilterShell>;
      if ($[20] !== t7) {
        t4 = <div className="md:col-start-1 md:row-start-1 md:row-span-2">{t7}</div>;
        $[20] = t7;
        $[21] = t4;
      } else {
        t4 = $[21];
      }
      t3 = "md:col-start-2 md:row-start-2 min-w-0 flex flex-col gap-8";
      T0 = TrendingCarousel;
      t2 = filtered.slice(0, 5);
      $[0] = anchorGenre;
      $[1] = filters.genres;
      $[2] = filters.q;
      $[3] = filters.status;
      $[4] = filters.tags;
      $[5] = stories;
      $[6] = tags;
      $[7] = T0;
      $[8] = filtered;
      $[9] = hasActiveFilters;
      $[10] = t2;
      $[11] = t3;
      $[12] = t4;
    } else {
      T0 = $[7];
      filtered = $[8];
      hasActiveFilters = $[9];
      t2 = $[10];
      t3 = $[11];
      t4 = $[12];
    }
    let t5;
    if ($[22] !== T0 || $[23] !== t2) {
      t5 = <T0 stories={t2} />;
      $[22] = T0;
      $[23] = t2;
      $[24] = t5;
    } else {
      t5 = $[24];
    }
    let t6;
    if ($[25] !== anchorSlug || $[26] !== filtered || $[27] !== hasActiveFilters) {
      t6 = filtered.length === 0 && hasActiveFilters ? <FilterEmptyState clearHref={`/genre/${anchorSlug}`} title="No novels match your filters." message="Try removing a genre, tag, or status filter to widen your results." /> : <StoryGrid stories={filtered} />;
      $[25] = anchorSlug;
      $[26] = filtered;
      $[27] = hasActiveFilters;
      $[28] = t6;
    } else {
      t6 = $[28];
    }
    let t7;
    if ($[29] !== t3 || $[30] !== t5 || $[31] !== t6) {
      t7 = <section className={t3}>{t5}{t6}</section>;
      $[29] = t3;
      $[30] = t5;
      $[31] = t6;
      $[32] = t7;
    } else {
      t7 = $[32];
    }
    let t8;
    if ($[33] !== t4 || $[34] !== t7) {
      t8 = <>{t4}{t7}</>;
      $[33] = t4;
      $[34] = t7;
      $[35] = t8;
    } else {
      t8 = $[35];
    }
    return t8;
  }
  const crownDiscoveryAvailable = crownDiscovery.status === "enabled";
  const crownDiscoveryData = crownDiscoveryAvailable ? crownDiscovery.data : EMPTY_CROWN_DISCOVERY;
  let discoveryOption;
  let filtered_0;
  let hasActiveFilters_0;
  let t2;
  let t3;
  let t4;
  if ($[36] !== crownDiscoveryAvailable || $[37] !== crownDiscoveryData || $[38] !== filters.discovery || $[39] !== filters.q || $[40] !== filters.status || $[41] !== filters.tags || $[42] !== stories || $[43] !== tags) {
    filtered_0 = filterCatalogStories(stories, {
      discovery: filters.discovery,
      status: filters.status,
      tags: filters.tags,
      q: filters.q
    }, crownDiscoveryData);
    let t5;
    if ($[50] !== filters.discovery) {
      t5 = filters.discovery ? crownDiscoveryFilterOption(filters.discovery) : undefined;
      $[50] = filters.discovery;
      $[51] = t5;
    } else {
      t5 = $[51];
    }
    discoveryOption = t5;
    const activeFilterCount_0 = filters.tags.length + (filters.status ? 1 : 0) + (filters.discovery ? 1 : 0);
    hasActiveFilters_0 = activeFilterCount_0 > 0 || Boolean(filters.q);
    t4 = <FilterShell activeCount={activeFilterCount_0}><GenreSidebar storyCount={filtered_0.length} activeDiscovery={filters.discovery} activeStatus={filters.status} activeTags={filters.tags} activeQuery={filters.q} showDiscoveryFilters={crownDiscoveryAvailable} tags={tags} /></FilterShell>;
    t2 = "flex-1 min-w-0 flex flex-col gap-8";
    t3 = !filters.discovery && <TrendingCarousel stories={filtered_0.slice(0, 5)} />;
    $[36] = crownDiscoveryAvailable;
    $[37] = crownDiscoveryData;
    $[38] = filters.discovery;
    $[39] = filters.q;
    $[40] = filters.status;
    $[41] = filters.tags;
    $[42] = stories;
    $[43] = tags;
    $[44] = discoveryOption;
    $[45] = filtered_0;
    $[46] = hasActiveFilters_0;
    $[47] = t2;
    $[48] = t3;
    $[49] = t4;
  } else {
    discoveryOption = $[44];
    filtered_0 = $[45];
    hasActiveFilters_0 = $[46];
    t2 = $[47];
    t3 = $[48];
    t4 = $[49];
  }
  let t5;
  if ($[52] !== discoveryOption?.label || $[53] !== discoveryOption?.shortLabel || $[54] !== discoveryOption?.source || $[55] !== filtered_0 || $[56] !== filters.discovery || $[57] !== filters.q || $[58] !== hasActiveFilters_0) {
    t5 = filtered_0.length === 0 && hasActiveFilters_0 ? <FilterEmptyState clearHref="/browse" title={filters.q ? `No novels match “${filters.q}”.` : "No novels match your filters."} message={filters.q ? "Try a different title or author, or clear your search." : filters.discovery ? "Try another Crown discovery filter or clear your filters." : "Try removing a tag or status filter to widen your results."} clearLabel={filters.q ? "Clear search \u2192" : "Clear filters \u2192"} /> : <StoryGrid stories={filtered_0} title={discoveryOption?.label} source={discoveryOption?.source} sortLabel={discoveryOption?.shortLabel} />;
    $[52] = discoveryOption?.label;
    $[53] = discoveryOption?.shortLabel;
    $[54] = discoveryOption?.source;
    $[55] = filtered_0;
    $[56] = filters.discovery;
    $[57] = filters.q;
    $[58] = hasActiveFilters_0;
    $[59] = t5;
  } else {
    t5 = $[59];
  }
  let t6;
  if ($[60] !== t2 || $[61] !== t3 || $[62] !== t5) {
    t6 = <section className={t2}>{t3}{t5}</section>;
    $[60] = t2;
    $[61] = t3;
    $[62] = t5;
    $[63] = t6;
  } else {
    t6 = $[63];
  }
  let t7;
  if ($[64] !== t4 || $[65] !== t6) {
    t7 = <>{t4}{t6}</>;
    $[64] = t4;
    $[65] = t6;
    $[66] = t7;
  } else {
    t7 = $[66];
  }
  return t7;
}